"use client";

import Link from "next/link";
import { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { BarChart3, BookOpenCheck, Clock3 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { getAptitudeReport, startAptitudeSession } from "@/lib/aptitude/api";
import type { AptitudeReport } from "@/lib/aptitude/types";

export default function AptitudeHomeClient() {
  const { user, loading: authLoading } = useAuth();
  const router = useRouter();
  const [report, setReport] = useState<AptitudeReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setReport(await getAptitudeReport());
    } catch {
      setError("データを取得できませんでした。");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading) void load();
  }, [authLoading, load]);

  const start = async (mode: "mock" | "review") => {
    if (mode === "mock") {
      const confirmed = window.confirm(
        "三十五分間の総合模試を開始します。回答確定後は前の問題へ戻れず、画面を閉じても時間は進みます。開始しますか？",
      );
      if (!confirmed) return;
    }
    setStarting(true);
    setError(null);
    try {
      const session = await startAptitudeSession(
        mode,
        mode === "review" ? report?.latest_result?.id : undefined,
      );
      router.push(`/aptitude/session/${session.id}`);
    } catch (requestError: unknown) {
      const status = (requestError as { response?: { status?: number } }).response?.status;
      setError(
        status === 503
          ? "現在、問題を準備しています。公開までしばらくお待ちください。"
          : "受検を開始できませんでした。もう一度お試しください。",
      );
    } finally {
      setStarting(false);
    }
  };

  if (authLoading || loading) {
    return <div className="mx-auto max-w-7xl p-8 text-center text-on-surface-variant">読み込み中…</div>;
  }

  if (!user) {
    return (
      <div className="mx-auto max-w-7xl px-5 py-16 text-center">
        <h1 className="text-3xl font-extrabold text-on-surface">無料の適性検査対策</h1>
        <p className="mt-4 leading-relaxed text-on-surface-variant">
          三十五分のオリジナル問題で実力を確認し、分野別の弱点と解説を確認できます。
        </p>
        <div className="mt-8 flex justify-center gap-3">
          <Link href="/login?next=/aptitude" className="rounded-lg border border-primary px-6 py-3 font-bold text-primary">ログイン</Link>
          <Link href="/signup?next=/aptitude" className="rounded-lg bg-primary px-6 py-3 font-bold text-white">無料で新規登録</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-8 px-5 py-8 md:py-12">
      <header>
        <p className="text-sm font-bold text-primary">オリジナル問題で練習</p>
        <h1 className="mt-1 text-3xl font-extrabold text-on-surface">適性検査対策</h1>
        <p className="mt-3 text-on-surface-variant">SPI対策にも活用できる能力検査の練習機能です。</p>
      </header>

      {error && (
        <div role="alert" className="rounded-lg border border-error/30 bg-error/5 p-4 text-sm text-error">
          {error}
          <button type="button" onClick={() => void load()} className="ml-3 font-bold underline">再読み込み</button>
        </div>
      )}

      <div className="grid gap-5 md:grid-cols-3">
        <ModeCard icon={<Clock3 />} title="三十五分総合模試" description="全十一分野、三十五問に挑戦します。" action="模試を開始" disabled={starting} onClick={() => void start("mock")} />
        <ModeCard icon={<BookOpenCheck />} title="弱点を復習" description="弱点上位三分野から十問を出題します。" action="復習を開始" disabled={starting || !report?.latest_result} onClick={() => void start("review")} />
        <ModeCard icon={<BarChart3 />} title="弱点レポート" description="分野別の正答率と所要時間を確認します。" action="レポートを見る" disabled={!report?.latest_result} onClick={() => router.push("/aptitude/report")} />
      </div>

      {report?.latest_result && (
        <section className="rounded-xl bg-surface-container-low p-5">
          <h2 className="font-bold text-on-surface">直近の総合模試</h2>
          <p className="mt-2 text-3xl font-extrabold text-primary">{report.latest_result.score_percent}<span className="ml-1 text-base text-on-surface">点</span></p>
          <Link href={`/aptitude/results/${report.latest_result.id}`} className="mt-3 inline-block text-sm font-bold text-primary underline">結果と解説を確認する</Link>
        </section>
      )}

      <p className="text-xs leading-relaxed text-on-surface-variant">「SPI」は株式会社リクルートマネジメントソリューションズの登録商標です。本機能は同社が提供する検査ではなく、独自に作成した練習問題です。</p>
    </div>
  );
}

function ModeCard({
  icon,
  title,
  description,
  action,
  disabled,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  action: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <section className="flex flex-col rounded-xl border border-border bg-surface p-5 custom-shadow">
      <div className="text-primary">{icon}</div>
      <h2 className="mt-4 text-lg font-bold text-on-surface">{title}</h2>
      <p className="mt-2 flex-1 text-sm leading-relaxed text-on-surface-variant">{description}</p>
      <button type="button" disabled={disabled} onClick={onClick} className="mt-5 min-h-11 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-white disabled:bg-outline-variant disabled:text-on-surface-variant">
        {action}
      </button>
    </section>
  );
}
